import React, { FC, ReactNode } from 'react'

import { Truncate } from '@ui/Truncate/Truncate'
import { RichIcon } from '@ui/components/RichIcon'
import { MT4Icon } from '@ui/icons/platforms/MT4.icon'
import { MT5Icon } from '@ui/icons/platforms/MT5.icon'
import { TMTIcon } from '@ui/icons/platforms/TMT.icon'
import { TradingViewIcon } from '@ui/icons/platforms/TradingView.icon'

import { TransactionType } from '@utils/transaction.utils'

import { PlatformTypeEnum } from '@model/PlatformTypeEnum'
import { TransactionDirection, TransactionDto } from '@model/TransactionDto'

import { TMGrayLogo } from '@assets/logo/TMGray.logo'

import { Box } from '@shared/components/Box'

import styles from './TransactionHistoryFromToCell.module.scss'

const getPlatformIcon = (platformTypeId: PlatformTypeEnum | undefined): ReactNode => {
  switch (platformTypeId) {
    case PlatformTypeEnum.MT4:
      return <MT4Icon size={24} />
    case PlatformTypeEnum.MT5:
      return <MT5Icon size={24} />
    case PlatformTypeEnum.TickmillTrader:
      return <TMTIcon size={24} />
    case PlatformTypeEnum.TradingView:
      return <TradingViewIcon size={24} />
    default:
      return null
  }
}

const getPaymentProviderLogo = (logoUrl: string | null | undefined): ReactNode => {
  if (!logoUrl) return null

  return (
    <RichIcon
      shape='circle'
      bg='surface-white'
      border='outline-low-em'
      bgImage={logoUrl}
      bgImageFit='contain'
      size='x32'
    />
  )
}

interface CellData {
  icon: ReactNode | null
  subtitle: string | null
}

interface NormalizedCellContent {
  label: string
  subtitle: string | null
}

export const normalizeFromToCellContent = (
  tt: TransactionType,
  direction: 'from' | 'to',
  label: string,
  subtitle: string | null
): NormalizedCellContent => {
  const isPaymentProviderSide =
    (tt === TransactionType.WalletDeposit && direction === 'from') ||
    (tt === TransactionType.WalletWithdrawal && direction === 'to')

  if (isPaymentProviderSide) {
    return {
      label,
      subtitle: null,
    }
  }

  return {
    label,
    subtitle,
  }
}

export const getFromToCellData = (
  tx: TransactionDto,
  originalDirection: 'from' | 'to'
): CellData => {
  const getWalletIcon = (): ReactNode => <TMGrayLogo className={styles.walletLogo} />
  switch (tx.type.id) {
    case TransactionType.WalletDeposit: {
      const wt = tx.walletTransactions[0]
      if (originalDirection === 'from') {
        const externalAccount =
          wt?.paymentProviderTransaction?.response?.update?.externalAccount || null
        return {
          icon: getPaymentProviderLogo(wt?.paymentProviderTransaction?.paymentProvider.logoUrl),
          subtitle: externalAccount,
        }
      }
      return { icon: <TMGrayLogo className={styles.walletLogo} />, subtitle: null }
    }

    case TransactionType.WalletWithdrawal: {
      const wt = tx.walletTransactions[0]
      if (originalDirection === 'to') {
        const externalAccount =
          wt?.paymentProviderTransaction?.response?.update?.externalAccount || null
        return {
          icon: getPaymentProviderLogo(wt?.paymentProviderTransaction?.paymentProvider.logoUrl),
          subtitle: externalAccount,
        }
      }
      return { icon: <TMGrayLogo className={styles.walletLogo} />, subtitle: null }
    }

    case TransactionType.WalletTransfer:
      return { icon: <TMGrayLogo className={styles.walletLogo} />, subtitle: null }

    case TransactionType.TransferFromTradingAccount:
    case TransactionType.InactivityTransfer: {
      if (originalDirection === 'from') {
        const ta = tx.tradingAccountTransactions[0]
        const platformTypeId = ta?.tradingAccount?.platformType?.id
        return {
          icon: getPlatformIcon(platformTypeId),
          subtitle: ta?.tradingAccount?.platformType?.name || null,
        }
      }
      return { icon: <TMGrayLogo className={styles.walletLogo} />, subtitle: null }
    }

    case TransactionType.TransferToTradingAccount: {
      if (originalDirection === 'to') {
        const ta = tx.tradingAccountTransactions[0]
        const platformTypeId = ta?.tradingAccount?.platformType?.id
        return {
          icon: getPlatformIcon(platformTypeId),
          subtitle: ta?.tradingAccount?.platformType?.name || null,
        }
      }
      return { icon: <TMGrayLogo className={styles.walletLogo} />, subtitle: null }
    }

    case TransactionType.DepositFromPaymentAgent: {
      if (originalDirection === 'from') {
        return { icon: null, subtitle: null }
      }
      return { icon: <TMGrayLogo className={styles.walletLogo} />, subtitle: null }
    }

    case TransactionType.WithdrawToPaymentAgent:
      return {
        icon: getWalletIcon(),
        subtitle: null,
      }
    case TransactionType.TradingAccountBalanceConversion: {
      const dir =
        originalDirection === 'from' ? TransactionDirection.Debit : TransactionDirection.Credit
      const ta = tx.tradingAccountTransactions.find((x) => x.directionType.id === dir)
      const platformTypeId = ta?.tradingAccount?.platformType?.id
      return {
        icon: getPlatformIcon(platformTypeId),
        subtitle: ta?.tradingAccount?.platformType?.name || null,
      }
    }

    case TransactionType.IBCommission:
    case TransactionType.MIBCommission:
    case TransactionType.MultiTierIBCommission:
    case TransactionType.IBRebate:
      return {
        icon: getWalletIcon(),
        subtitle: null,
      }
    case TransactionType.BankFeesRefund:
    case TransactionType.LoyaltyRewardPayout:
    case TransactionType.MonthlyInterestTransfer:
    case TransactionType.Bonus:
    case TransactionType.WelcomeAccountReward:
    case TransactionType.Refund:
    case TransactionType.CopyTraderFees:
    case TransactionType.TradingIssueCompensation:
    case TransactionType.ArchivedAccountTransfer:
      return {
        icon: originalDirection === 'to' ? getWalletIcon() : null,
        subtitle: null,
      }

    case TransactionType.FeeDeduction:
    case TransactionType.InactivityFee: {
      if (originalDirection === 'from') {
        return { icon: <TMGrayLogo className={styles.walletLogo} />, subtitle: null }
      }
      return { icon: null, subtitle: null }
    }
    case TransactionType.ManualAdjustment:
      return {
        icon: getWalletIcon(),
        subtitle: null,
      }

    case TransactionType.Reverse: {
      const ta =
        tx.tradingAccountTransactions.length === 1 ? tx.tradingAccountTransactions[0] : undefined

      if (ta) {
        const taDirection = ta.directionType.id === TransactionDirection.Debit ? 'from' : 'to'

        if (originalDirection === taDirection) {
          return {
            icon: getPlatformIcon(ta.tradingAccount?.platformType?.id),
            subtitle: ta.tradingAccount?.platformType?.name || null,
          }
        }

        return {
          icon: <TMGrayLogo className={styles.walletLogo} />,
          subtitle: null,
        }
      }

      const wt = tx.walletTransactions[0]

      if (wt) {
        return {
          icon: <TMGrayLogo className={styles.walletLogo} />,
          subtitle: null,
        }
      }

      return {
        icon: null,
        subtitle: null,
      }
    }
    default:
      return { icon: null, subtitle: null }
  }
}

interface Props {
  label: string
  icon: ReactNode | null
  subtitle: string | null
  hasTruncate?: boolean
}

export const TransactionHistoryFromToCell: FC<Props> = ({ label, icon, subtitle, hasTruncate }) => {
  if (!label) return null

  const renderText = (text: string, className: string) =>
    hasTruncate ? (
      <Truncate>
        <span className={className}>{text}</span>
      </Truncate>
    ) : (
      <span className={className}>{text}</span>
    )

  return (
    <Box
      flex
      align='center'
      gap='x8'
      justify={hasTruncate ? 'flex-start' : 'center'}
      fullWidth={hasTruncate}
      minWidth={hasTruncate ? 0 : undefined}
    >
      {icon}
      <div className={styles.content}>
        {renderText(label, styles.label)}
        {subtitle && renderText(subtitle, styles.subtitle)}
      </div>
    </Box>
  )
}
